// SPDX-License-Identifier: AGPL-3.0-or-later
import { customName } from "./categories.js";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { formatDate, formatMoney, formatPercent, translate, type Locale, type MessageKey, type Params } from "@almanac/i18n";

export interface I18n {
  locale: Locale;
  t: (key: MessageKey, params?: Params) => string;
  money: (cents: number, compact?: boolean) => string;
  percent: (value: number, digits?: number) => string;
  date: (iso: string, style?: "short" | "long" | "month" | "weekday") => string;
  /** A category's own name, e.g. "Water". */
  category: (id: string) => string;
  /** A category with its parents, e.g. "Housing › Utilities › Water". */
  categoryPath: (id: string) => string;
}

const Context = createContext<I18n | null>(null);

function label(locale: Locale, id: string): string {
  if (id === "rest") return translate(locale, "pie.other");
  const custom = customName(id);
  if (custom) return custom;
  const text = translate(locale, `cat.${id}` as MessageKey);
  // An unknown id (from a newer version, say) shows its last segment rather than a raw key.
  return text === `cat.${id}` ? id.slice(id.lastIndexOf("/") + 1) : text;
}

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo<I18n>(
    () => ({
      locale,
      t: (key, params) => translate(locale, key, params),
      money: (cents, compact) => formatMoney(cents, locale, "CAD", compact),
      percent: (value, digits) => formatPercent(value, locale, digits),
      date: (iso, style) => formatDate(iso, locale, style),
      category: (id) => label(locale, id),
      categoryPath: (id) =>
        id
          .split("/")
          .map((_, i, parts) => label(locale, parts.slice(0, i + 1).join("/")))
          .join(" › "),
    }),
    [locale],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(Context);
  if (!value) throw new Error("useI18n outside I18nProvider");
  return value;
}
